import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { InputComponent } from '@/shared/ui-components/input/input.component';
import { SelectComponent } from '@/shared/ui-components/select/select.component';
import { KeyedOption } from '@/shared/enums/generic.enum';
import { LanguageService } from '@/shared/services/language.service';
import { AskQuestionTopic, DealQuestionType, GeneralQuestionCategory } from '../../data-access/questions.enum';
import { askQuestionTopicOptions, dealQuestionTypeOptions } from '../../data-access/questions.option';
import { QuestionsService } from '../../questions.service';

/**
 * Raise form shared by the Questions page dialog and the global "Talk to us" drawer.
 *
 * The topic decides which endpoint runs, and the fields follow that endpoint rather than
 * a shared shape: a deal question is a single `question` plus a required `type`, a general
 * question is `subject` + `details`.
 */
@Component({
    selector: 'app-ask-question-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, ButtonComponent, InputComponent, SelectComponent],
    templateUrl: './ask-question-form.component.html'
})
export class AskQuestionFormComponent {
    @Output() public submitted = new EventEmitter<void>();
    @Output() public cancelled = new EventEmitter<void>();

    public readonly form: FormGroup;
    public readonly topic = signal<AskQuestionTopic>(AskQuestionTopic.DEAL);

    public readonly _questionsService = inject(QuestionsService);

    private readonly _formBuilder = inject(FormBuilder);
    private readonly _translateService = inject(TranslateService);
    private readonly _languageService = inject(LanguageService);

    public readonly topicOptions = computed(() => this.translateOptions(askQuestionTopicOptions));
    public readonly typeOptions = computed(() => this.translateOptions(dealQuestionTypeOptions));

    /** The deals this caller may raise a question about, as decided by the API. */
    public readonly dealOptions = computed(() => this._questionsService.dealOptions());

    public readonly isDealTopic = computed(() => this.topic() === AskQuestionTopic.DEAL);
    public readonly hasNoSelectableDeals = computed(() => this.isDealTopic() && this.dealOptions().length === 0);

    constructor() {
        this.form = this._formBuilder.group({
            topic: [AskQuestionTopic.DEAL, Validators.required],
            dealOpportunityId: [null as number | null, Validators.required],
            type: [DealQuestionType.OTHER, Validators.required],
            question: ['', [Validators.required, Validators.maxLength(4000)]],
            subject: [''],
            details: ['']
        });

        this._questionsService.loadDealOptions();

        // `SelectComponent` is a ControlValueAccessor with no ngModelChange output, so the
        // control itself is the only reliable place to react to a topic switch.
        this.form
            .get('topic')!
            .valueChanges.pipe(takeUntilDestroyed())
            .subscribe((value: AskQuestionTopic) => {
                this.topic.set(value);
                this.applyTopicValidators(value);
            });
    }

    public reset(): void {
        this.topic.set(AskQuestionTopic.DEAL);
        this.form.reset({
            topic: AskQuestionTopic.DEAL,
            dealOpportunityId: null,
            type: DealQuestionType.OTHER,
            question: '',
            subject: '',
            details: ''
        });
        this.applyTopicValidators(AskQuestionTopic.DEAL);
    }

    public cancel(): void {
        this.reset();
        this.cancelled.emit();
    }

    public submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.getRawValue() as {
            topic: AskQuestionTopic;
            dealOpportunityId: number | null;
            type: DealQuestionType;
            question: string;
            subject: string;
            details: string;
        };

        const onSuccess = () => {
            this.reset();
            this.submitted.emit();
        };

        if (raw.topic === AskQuestionTopic.DEAL && raw.dealOpportunityId != null) {
            this._questionsService.raiseDealQuestion(raw.dealOpportunityId, { type: raw.type, question: raw.question.trim() }, onSuccess);
            return;
        }

        this._questionsService.raiseGeneralQuestion(
            {
                category: raw.topic === AskQuestionTopic.COMPLIANCE_ONBOARDING ? GeneralQuestionCategory.COMPLIANCE_ONBOARDING : GeneralQuestionCategory.OTHER,
                subject: raw.subject.trim(),
                details: raw.details.trim()
            },
            onSuccess
        );
    }

    /** Each branch validates only the fields its endpoint actually accepts. */
    private applyTopicValidators(topic: AskQuestionTopic): void {
        const isDeal = topic === AskQuestionTopic.DEAL;

        this.setValidators('dealOpportunityId', isDeal ? [Validators.required] : []);
        this.setValidators('type', isDeal ? [Validators.required] : []);
        this.setValidators('question', isDeal ? [Validators.required, Validators.maxLength(4000)] : []);
        this.setValidators('subject', isDeal ? [] : [Validators.required, Validators.maxLength(200)]);
        this.setValidators('details', isDeal ? [] : [Validators.required, Validators.maxLength(4000)]);
    }

    private setValidators(controlName: string, validators: Validators[]): void {
        const control = this.form.get(controlName);
        if (!control) return;
        control.setValidators(validators as never);
        control.updateValueAndValidity({ emitEvent: false });
    }

    private translateOptions<T>(options: KeyedOption<T>[]): Array<{ label: string; value: T }> {
        if (!this._languageService.ready()) return [];
        return options.map((option) => ({
            label: this._translateService.instant(option.labelKey) as string,
            value: option.value
        }));
    }
}
